import React from "react";
import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/dashboard/Navbar";
import {
  TrendingUp,
  Clock,
  ArrowLeft,
  Sparkles,
  Zap,
  Activity,
  Users,
  PieChart,
  Filter,
} from "lucide-react";

const BulkMessageReport = () => {
  const navigate = useNavigate();

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-red-50 to-red-70">
        <div className="px-4 py-8 mx-auto max-w-4xl sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white rounded-lg shadow-sm hover:bg-gray-50 transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </motion.button>

            <div className="flex justify-center mb-6">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="p-6 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl shadow-xl"
              >
                <Users className="w-12 h-12 text-white" />
              </motion.div>
            </div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl font-bold text-gray-900 mb-4"
            >
              Bulk Message Report
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-xl text-gray-600 mb-8"
            >
              Advanced analytics and insights for your bulk messaging campaigns
            </motion.p>
          </motion.div>

          {/* Coming Soon Card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 px-8 py-12 text-center text-white relative overflow-hidden">
              <div className="absolute inset-0 bg-black opacity-10"></div>
              <div className="relative z-10">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{
                    duration: 20,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                  className="w-20 h-20 mx-auto mb-6 bg-white bg-opacity-20 rounded-full flex items-center justify-center"
                >
                  <Clock className="w-10 h-10" />
                </motion.div>

                <h2 className="text-3xl font-bold mb-4">Coming Soon</h2>
                <p className="text-xl opacity-90">
                  We're building powerful bulk messaging analytics for you
                </p>
              </div>
            </div>

            {/* Features Preview */}
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="text-center p-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border border-blue-100"
                >
                  <div className="w-12 h-12 mx-auto mb-4 bg-blue-500 rounded-xl flex items-center justify-center">
                    <PieChart className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Campaign Analytics
                  </h3>
                  <p className="text-sm text-gray-600">
                    Track delivery rates, engagement, and conversion metrics
                    across campaigns
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className="text-center p-6 bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl border border-purple-100"
                >
                  <div className="w-12 h-12 mx-auto mb-4 bg-purple-500 rounded-xl flex items-center justify-center">
                    <Filter className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Advanced Filtering
                  </h3>
                  <p className="text-sm text-gray-600">
                    Filter reports by date, audience, message type, and
                    performance metrics
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="text-center p-6 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border border-orange-100"
                >
                  <div className="w-12 h-12 mx-auto mb-4 bg-orange-500 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Performance Trends
                  </h3>
                  <p className="text-sm text-gray-600">
                    Analyze trends over time and optimize your bulk messaging
                    strategy
                  </p>
                </motion.div>
              </div>

              {/* Progress Bar */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">
                    Development Progress
                  </span>
                  <span className="text-sm text-gray-500">85%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: "85%" }}
                    transition={{ duration: 2, delay: 1 }}
                    className="bg-gradient-to-r from-red-500 to-red-600 h-3 rounded-full"
                  ></motion.div>
                </div>
              </div>

              {/* Notification */}
              <div className="bg-gradient-to-r from-red-50 to-red-50 border border-red-200 rounded-xl p-6 text-center">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <Sparkles className="w-5 h-5 text-red-600" />
                  <span className="font-semibold text-red-900">
                    Stay Updated
                  </span>
                </div>
                <p className="text-red-700">
                  We'll notify you as soon as the Bulk Message Report feature is
                  ready!
                </p>
              </div>
            </div>
          </motion.div>

          {/* Floating Elements */}
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-20 left-10 opacity-20"
          >
            <Activity className="w-8 h-8 text-red-400" />
          </motion.div>

          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1,
            }}
            className="absolute top-40 right-10 opacity-20"
          >
            <Zap className="w-6 h-6 text-red-400" />
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default BulkMessageReport;
